import React, { ReactNode, useContext, useEffect, useRef } from 'react';
import useKeyPress from '../utils/useKeypress';
import GameContext from '../context/GameContext';
import useOnScreen from '../utils/useOnScreen';
import { Button } from 'react-bootstrap';
import { stockfishEngine } from '../utils/StockfishEngine';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleLeft,
  faAngleRight,
  faAnglesLeft,
  faAnglesRight,
  faUpDown,
  faHandBackFist,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';

// keyboardNav=false disables the arrow-key handling for this instance — used by the second copy of
// the controls in the move panel so a keypress doesn't step the board twice.
export default function GamesListItemControls(props: { keyboardNav?: boolean } = {}): ReactNode {
  const { keyboardNav = true } = props;
  const elementRef = useRef<HTMLDivElement>(null);
  const isVisible = useOnScreen(elementRef);
  const { chessviewerRef, flip, dragging, setDragging } = useContext(GameContext);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const leftPress = useKeyPress('ArrowLeft');
  const rightPress = useKeyPress('ArrowRight');
  const upPress = useKeyPress('ArrowUp');
  const downPress = useKeyPress('ArrowDown');

  useEffect(() => {
    const chessviewer = chessviewerRef.current;
    const anyPress = leftPress || rightPress || upPress || downPress;
    if (chessviewer == null || !isVisible || !anyPress || !keyboardNav) {
      return;
    }
    // Up/Down jump two half-moves so you land on the same side's next move.
    const direction = leftPress || upPress ? 'previous' : 'next';
    const count = upPress || downPress ? 2 : 1;
    const step = () => {
      for (let i = 0; i < count; i++) {
        chessviewer.go(direction);
      }
    };
    // Move once immediately, then keep moving while the arrow key is held.
    step();
    const initialDelay = setTimeout(() => {
      const interval = setInterval(step, 90);
      // Stash the interval on the timeout cleanup path.
      intervalRef.current = interval;
    }, 300);
    return () => {
      clearTimeout(initialDelay);
      if (intervalRef.current != null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [chessviewerRef, isVisible, leftPress, rightPress, upPress, downPress, keyboardNav]);
  const chessviewer = chessviewerRef.current;

  const toggleDragging = () => {
    if (chessviewer == null) {
      return;
    }
    const next = !chessviewer.isDraggable();
    chessviewer.setDraggable(next);
    // Chessviewer.onDraggableChange also syncs this, but set immediately for snappy feedback.
    setDragging(next);
    // Warm the local engine up front so the first dragged move isn't also paying the load cost.
    if (next) {
      stockfishEngine.preload();
    }
  };

  return (
    <div ref={elementRef} className="cm-board-controls d-flex justify-content-center">
      {chessviewer != null ? (
        <>
          <Button
            onClick={toggleDragging}
            className="cm-board-ctrl-btn p-1"
            variant={dragging ? 'primary' : 'secondary'}
            active={dragging}
            title={
              dragging
                ? 'Piece dragging on. Tap to disable and scroll freely'
                : 'Enable dragging pieces on the board'
            }
          >
            <FontAwesomeIcon icon={dragging ? faXmark : faHandBackFist} />
          </Button>
          <Button
            onClick={flip}
            className="cm-board-ctrl-btn p-1"
            variant="secondary"
            title="Switch sides. Review the other player"
          >
            <FontAwesomeIcon icon={faUpDown} />
          </Button>
          <Button
            onClick={() => chessviewer.go('first')}
            className="cm-board-ctrl-btn p-1"
            variant="secondary"
          >
            <FontAwesomeIcon icon={faAnglesLeft} />
          </Button>
          <Button
            onClick={() => chessviewer.go('previous')}
            className="cm-board-ctrl-btn p-1"
            variant="secondary"
          >
            <FontAwesomeIcon icon={faAngleLeft} />
          </Button>
          <Button
            onClick={() => chessviewer.go('next')}
            className="cm-board-ctrl-btn p-1"
            variant="secondary"
          >
            <FontAwesomeIcon icon={faAngleRight} />
          </Button>
          <Button
            onClick={() => chessviewer.go('last')}
            className="cm-board-ctrl-btn p-1"
            variant="secondary"
          >
            <FontAwesomeIcon icon={faAnglesRight} />
          </Button>
        </>
      ) : null}
    </div>
  );
}
